import React, { useState, useRef, useEffect } from 'react';
import classnames from 'classnames';

export interface Option {
  id: string;
  label: string;
  selected: boolean;
}

export interface MultiSelectDropdownProps {
  options: Option[];
  placeholder: string;
  labelApply: string;
  labelReset: string;
  handleApplyFilters: (selectedOptions: Option[]) => void;
  handleResetFilters: () => void;
}

export default function MultiSelectDropdown({
  options: originalOptions,
  placeholder,
  labelApply,
  labelReset,
  handleApplyFilters,
  handleResetFilters
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<Option[]>(originalOptions);

  const prevSelectedOptions = useRef<Option[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleCheckboxChange = (option: Option) => {
    setOptions(prev =>
      prev.map(item => (item.id === option.id ? { ...item, selected: !item.selected } : item))
    );
  };

  const applyFilters = () => {
    if (JSON.stringify(prevSelectedOptions.current) !== JSON.stringify(options)) {
      handleApplyFilters(options);
      prevSelectedOptions.current = options;
    }

    setIsOpen(false);
  };

  const resetFilters = () => {
    setOptions(prev => prev.map(item => ({ ...item, selected: false })));
    handleResetFilters();
  };

  useEffect(() => {
    setOptions(originalOptions);
  }, [originalOptions]);

  return (
    <div className="dropdown" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="dropdown__trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{placeholder}</span>
      </button>

      <div
        className={classnames('dropdown__content', { 'dropdown__content--open': isOpen })}
        role="listbox"
      >
        <div className="dropdown__options">
          {options?.map((option, index) => {
            const checkboxId = `checkbox-${option.id}`;
            return (
              <div className="checkbox-with-icon" key={index}>
                <input
                  type="checkbox"
                  checked={option.selected}
                  onChange={() => handleCheckboxChange(option)}
                  aria-checked={option.selected}
                  id={checkboxId}
                />
                <label htmlFor={checkboxId}>
                  <span>{option.label}</span>
                </label>
              </div>
            );
          })}
        </div>

        <div className="dropdown__actions">
          <button onClick={applyFilters} className="btn btn--primary dropdown__apply">
            {labelApply}
          </button>
          <button onClick={resetFilters} className="btn btn--secondary dropdown__reset">
            {labelReset}
          </button>
        </div>
      </div>
    </div>
  );
}
